import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Card, CardContent } from '../ui/card';
import { EmployeePopover } from '../ui/employee-popover';
import { GET_PROJECT_COLUMNS, GET_PROJECT_TASKS } from '../../graphql/queries';
import { TaskCardData } from './TaskCard';
import { TaskDialog, TaskDialogTask } from './TaskDialog';
import {
  TASK_PRIORITY_CLASSES,
  TASK_PRIORITY_LABELS,
  formatDate,
  getInitials,
} from './projectUtils';

interface ProjectTaskListProps {
  projectId: string;
}

interface TaskRow extends TaskCardData {
  columnName: string;
}

export const ProjectTaskList: React.FC<ProjectTaskListProps> = ({
  projectId,
}) => {
  const { data: columnsData } = useQuery<{
    projectColumns: Array<{ id: string; name: string }>;
  }>(GET_PROJECT_COLUMNS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const { data: tasksData, loading } = useQuery<{
    projectTasks: TaskCardData[];
  }>(GET_PROJECT_TASKS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const [editingTask, setEditingTask] = useState<TaskDialogTask | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const columnNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    (columnsData?.projectColumns || []).forEach((c) => {
      map[c.id] = c.name;
    });
    return map;
  }, [columnsData]);

  const rows: TaskRow[] = useMemo(() => {
    return (tasksData?.projectTasks || [])
      .map((t) => ({
        ...t,
        columnName: columnNameMap[t.columnId] || '—',
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
  }, [tasksData, columnNameMap]);

  const handleRowClick = (row: TaskRow) => {
    setEditingTask({
      id: row.id,
      name: row.name,
      description: row.description,
      priority: row.priority,
      startDate: row.startDate,
      plannedEndDate: row.plannedEndDate,
      columnId: row.columnId,
      assignees: row.assignees || [],
    });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left bg-muted/30">
                  <th className="py-2 px-3 font-medium">Nazwa</th>
                  <th className="py-2 px-3 font-medium">Priorytet</th>
                  <th className="py-2 px-3 font-medium">Kolumna</th>
                  <th className="py-2 px-3 font-medium">Przypisane</th>
                  <th className="py-2 px-3 font-medium">Rozpoczęcie</th>
                  <th className="py-2 px-3 font-medium">Termin</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Brak zadań w tym projekcie
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const assignees = row.assignees || [];
                    return (
                      <tr
                        key={row.id}
                        className="border-b hover:bg-accent/30 cursor-pointer"
                        onClick={() => handleRowClick(row)}
                      >
                        <td className="py-2 px-3 font-medium">{row.name}</td>
                        <td className="py-2 px-3">
                          <span
                            className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                              TASK_PRIORITY_CLASSES[row.priority]
                            }`}
                          >
                            {TASK_PRIORITY_LABELS[row.priority]}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {row.columnName}
                        </td>
                        <td className="py-2 px-3">
                          {assignees.length === 0 ? (
                            <span className="text-xs text-muted-foreground italic">
                              nieprzypisane
                            </span>
                          ) : (
                            <div className="flex -space-x-1.5">
                              {assignees.slice(0, 4).map((a) => {
                                const emp = a.employee;
                                const avatar = (
                                  <div className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-semibold flex items-center justify-center border border-background">
                                    {getInitials(emp?.firstName, emp?.lastName)}
                                  </div>
                                );
                                if (!emp) {
                                  return <div key={a.id}>{avatar}</div>;
                                }
                                return (
                                  <EmployeePopover
                                    key={a.id}
                                    employeeId={emp.id}
                                    fallbackName={`${emp.firstName} ${emp.lastName}`}
                                  >
                                    {avatar}
                                  </EmployeePopover>
                                );
                              })}
                              {assignees.length > 4 && (
                                <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold flex items-center justify-center border border-background">
                                  +{assignees.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {formatDate(row.startDate)}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {formatDate(row.plannedEndDate)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <TaskDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingTask(null);
        }}
        projectId={projectId}
        task={editingTask}
        columns={(columnsData?.projectColumns || []).map((c) => ({
          id: c.id,
          name: c.name,
        }))}
      />
    </div>
  );
};
